import { isDefAndNotNull } from '@utils/def';
import _ from 'lodash';
import { v4 } from 'uuid';

import { LegTerms } from 'models/LegTerms';
import { ActionType, getType } from 'typesafe-actions';

import { HedgingInstrumentInterestRateMode } from '../../../../../../models/HedgingInstrument/enum';
import { LegTypes } from '../../../../../../models/Leg/enum';
import { getCurrentNotionalValueForLeg } from '../../../../utils';

import * as pricerActions from '../../actions';
import {
  EXCLUDED_FIELDS_FOR_COPY_PASTE,
  EXCLUDED_FIELDS_FOR_COPY_PASTE_FLOWS,
} from '../../constants';
import { PricerActionType } from '../../pricerReducer';

import FlowsReducer, { FlowsActionType, FlowsState } from '../FlowsReducer';
import {
  getDefaultDiscountingIndexFromCurrencyConventions,
  getDefaultForecastingIndexFromCurrencyConventions,
  getDefaultResetCalendarIdByForecastingIndex,
} from '../MasterLegReducer/utils';

import * as flowsActions from './../FlowsReducer/actions';
import * as legsActions from './actions';
import { empty_leg_terms } from './constants';

type LegsActionType = ActionType<typeof legsActions>;

export type LegsState = {
  [key: string]: {
    leg_terms: LegTerms;
    leg_metrics: [];
    leg_valuation: [];
    leg_flows: FlowsState;
  };
};

const initialState: LegsState = {};

const legsReducer = (
  state: LegsState = initialState,
  action: LegsActionType | PricerActionType | FlowsActionType,
): LegsState => {
  switch (action.type) {
    case getType(pricerActions.buildSchedule.request):
    case getType(pricerActions.buildSchedule.failure):
      return {
        ...state,
      };
    case getType(pricerActions.buildSchedule.success): {
      const stateWithFlows = action.payload.flows.reduce(
        (acc: LegsState, flows, index) => {
          acc[index] = { ...state[index], leg_flows: flows };
          return acc;
        },
        {},
      );

      return {
        ...state,
        ...stateWithFlows,
      };
    }

    case getType(legsActions.updateLegTermsStateFromForm): {
      return {
        ...state,
        [action.payload.legNumber]: {
          ...state[action.payload.legNumber],
          leg_terms: {
            ...state[action.payload.legNumber].leg_terms,
            ...action.payload.data,
          },
        },
      };
    }

    case getType(pricerActions.updateFxRateLegNotional): {
      return {
        ...state,
        [0]: {
          ...state[0],
          leg_terms: {
            ...state[0].leg_terms,
            notional: action.payload.leftLegNotional,
          },
        },
        [1]: {
          ...state[1],
          leg_terms: {
            ...state[1].leg_terms,
            notional: action.payload.rightLegNotional,
          },
        },
      };
    }

    case getType(legsActions.updateAllLegTermsStateFromForm): {
      const newState = { ...state };
      const updatedLegsState = _.mapValues(newState, (leg) => ({
        ...leg,
        leg_terms: {
          ...leg.leg_terms,
          ...action.payload.data,
        },
      }));

      return {
        ...state,
        ...updatedLegsState,
      };
    }

    case getType(legsActions.addFixedLeg): {
      const lastId = Object.keys(state).length;
      const defaultIndexId = getDefaultDiscountingIndexFromCurrencyConventions(
        action.payload.leg_currency_id,
        action.payload.currency_conventions,
      );
      //TODO: clean old props
      return {
        ...state,
        [lastId]: {
          ...state[lastId],
          leg_terms: {
            ...empty_leg_terms,
            transaction_leg_id: v4(),
            current_notional_leg: getCurrentNotionalValueForLeg(
              action.payload.instrument_type_id!,
              LegTypes.fixed,
              lastId,
            ),
            leg_type_id: LegTypes.fixed,
            pay_receive_id: action.payload.pay_receive_id,
            notional: action.payload.master_leg_terms.notional,
            currency_id: action.payload.leg_currency_id
              ? action.payload.leg_currency_id
              : action.payload.master_leg_terms.currency_id,
            collateral_currency:
              action.payload.master_leg_terms.collateral_currency,
            discounting_index_id: defaultIndexId,
            day_count_id: action.payload.master_leg_terms.day_count_id,
            initial_notional_exchange:
              action.payload.master_leg_terms.initial_notional_exchange,
            intermediary_notional_exchange:
              action.payload.master_leg_terms.intermediary_notional_exchange,
            final_notional_exchange:
              action.payload.master_leg_terms.final_notional_exchange,
            // Dates
            effective_date: action.payload.master_leg_terms.effective_date,
            maturity_date: action.payload.master_leg_terms.maturity_date,
            settlement_date: action.payload.master_leg_terms.settlement_date,
            pay_frequency: action.payload.master_leg_terms.pay_frequency,
            //pay_start_end: action.payload.master_leg_terms.pay_start_end,
            payment_offset: action.payload.master_leg_terms.payment_offset,
            business_day_convention:
              action.payload.master_leg_terms.business_day_convention,
            calendar_currencies:
              action.payload.master_leg_terms.calendar_currencies,
            // Fixed Leg Fields
            calc_basis_id: 1, // MONEY MARKET
            coupon: 1.75,
            // Floating Leg Fields
            forecasting_index_id: null,
            spread: null,
            // Cap Floor Fields
            strike: null,
            //volatility: null,
            // Inflation Fields
            //inflation_index: null,
            //initial_index: null,
            initial_payment: null,
            // schedule roll fields
            toggle_roll_fields_flag:
              action.payload.master_leg_terms.toggle_roll_fields_flag,
            eom_roll: action.payload.master_leg_terms.eom_roll,
            first_payment_date:
              action.payload.master_leg_terms.first_payment_date,
            next_to_last_payment_date:
              action.payload.master_leg_terms.next_to_last_payment_date,

            calculation_calendar:
              action.payload.master_leg_terms.calculation_calendar,
            reset_calendar: null,
          },
          leg_metrics: [],
          leg_valuation: [],
          leg_flows: [],
        },
      };
    }
    case getType(legsActions.addFloatLeg): {
      const lastId = Object.keys(state).length;
      const defaultForecastingIndexId =
        getDefaultForecastingIndexFromCurrencyConventions(
          action.payload.leg_currency_id,
          action.payload.currency_conventions,
        );
      const defaultDiscountIndexId =
        getDefaultDiscountingIndexFromCurrencyConventions(
          action.payload.leg_currency_id,
          action.payload.currency_conventions,
        );
      const defaultResetCalendarId =
        getDefaultResetCalendarIdByForecastingIndex(
          defaultForecastingIndexId,
          action.payload.ir_indices,
        );

      return {
        ...state,
        [lastId]: {
          ...state[lastId],
          leg_terms: {
            ...empty_leg_terms,
            transaction_leg_id: v4(),
            current_notional_leg: getCurrentNotionalValueForLeg(
              action.payload.instrument_type_id!,
              LegTypes.float,
              lastId,
            ),
            leg_type_id: LegTypes.float,
            pay_receive_id: action.payload.pay_receive_id,
            notional: action.payload.master_leg_terms.notional,
            currency_id: action.payload.leg_currency_id
              ? action.payload.leg_currency_id
              : action.payload.master_leg_terms.currency_id,
            collateral_currency:
              action.payload.master_leg_terms.collateral_currency,
            discounting_index_id: defaultDiscountIndexId,
            day_count_id: action.payload.master_leg_terms.day_count_id,
            initial_notional_exchange:
              action.payload.master_leg_terms.initial_notional_exchange,
            intermediary_notional_exchange:
              action.payload.master_leg_terms.intermediary_notional_exchange,
            final_notional_exchange:
              action.payload.master_leg_terms.final_notional_exchange,
            // Dates
            effective_date: action.payload.master_leg_terms.effective_date,
            maturity_date: action.payload.master_leg_terms.maturity_date,
            settlement_date: action.payload.master_leg_terms.settlement_date,
            pay_frequency: action.payload.master_leg_terms.pay_frequency,
            //pay_start_end: action.payload.master_leg_terms.pay_start_end,
            payment_offset: action.payload.master_leg_terms.payment_offset,
            business_day_convention:
              action.payload.master_leg_terms.business_day_convention,
            calendar_currencies:
              action.payload.master_leg_terms.calendar_currencies,
            // Fixed Leg Fields
            calc_basis_id: null,
            coupon: null,
            // Floating Leg Fields
            forecasting_index_id: defaultForecastingIndexId,
            spread: 0.0,
            // Cap Floor Fields
            strike: null,
            volatility: null,
            // Inflation Fields
            //inflation_index: null,
            //initial_index: null,
            initial_payment: null,
            // schedule roll fields
            toggle_roll_fields_flag:
              action.payload.master_leg_terms.toggle_roll_fields_flag,
            eom_roll: action.payload.master_leg_terms.eom_roll,
            first_payment_date:
              action.payload.master_leg_terms.first_payment_date,
            next_to_last_payment_date:
              action.payload.master_leg_terms.next_to_last_payment_date,

            calculation_calendar:
              action.payload.master_leg_terms.calculation_calendar,
            reset_calendar: [defaultResetCalendarId],
          },
          leg_metrics: [],
          leg_valuation: [],
          leg_flows: [],
        },
      };
    }
    case getType(legsActions.addFloorLeg): {
      const lastId = Object.keys(state).length;
      const defaultForecastingIndexId =
        getDefaultForecastingIndexFromCurrencyConventions(
          action.payload.leg_currency_id,
          action.payload.currency_conventions,
        );
      const defaultResetCalendarId =
        getDefaultResetCalendarIdByForecastingIndex(
          defaultForecastingIndexId,
          action.payload.ir_indices,
        );
      const defaultDiscountIndexId =
        getDefaultDiscountingIndexFromCurrencyConventions(
          action.payload.leg_currency_id,
          action.payload.currency_conventions,
        );

      return {
        ...state,
        [lastId]: {
          ...state[lastId],
          leg_terms: {
            ...empty_leg_terms,
            transaction_leg_id: v4(),
            current_notional_leg: getCurrentNotionalValueForLeg(
              action.payload.instrument_type_id!,
              LegTypes.floor,
              lastId,
            ),
            leg_type_id: LegTypes.floor,
            pay_receive_id: action.payload.pay_receive_id,
            notional: action.payload.master_leg_terms.notional,
            currency_id: action.payload.leg_currency_id
              ? action.payload.leg_currency_id
              : action.payload.master_leg_terms.currency_id,
            collateral_currency:
              action.payload.master_leg_terms.collateral_currency,
            discounting_index_id: defaultDiscountIndexId,
            day_count_id: action.payload.master_leg_terms.day_count_id,
            initial_notional_exchange:
              action.payload.master_leg_terms.initial_notional_exchange,
            intermediary_notional_exchange:
              action.payload.master_leg_terms.intermediary_notional_exchange,
            final_notional_exchange:
              action.payload.master_leg_terms.final_notional_exchange,
            // Dates
            effective_date: action.payload.master_leg_terms.effective_date,
            maturity_date: action.payload.master_leg_terms.maturity_date,
            settlement_date: action.payload.master_leg_terms.settlement_date,
            pay_frequency: action.payload.master_leg_terms.pay_frequency,
            //pay_start_end: action.payload.master_leg_terms.pay_start_end,
            payment_offset: action.payload.master_leg_terms.payment_offset,
            business_day_convention:
              action.payload.master_leg_terms.business_day_convention,
            calendar_currencies:
              action.payload.master_leg_terms.calendar_currencies,
            // Fixed Leg Fields
            calc_basis_id: null,
            coupon: null,
            // Floating Leg Fields
            forecasting_index_id: defaultForecastingIndexId,
            spread: 0.0,
            // Cap Floor Fields
            strike: 1.7,
            volatility: 0.0,
            // Inflation Fields
            //inflation_index: null,
            //initial_index: null,
            initial_payment: null,
            // schedule roll fields
            toggle_roll_fields_flag:
              action.payload.master_leg_terms.toggle_roll_fields_flag,
            eom_roll: action.payload.master_leg_terms.eom_roll,
            first_payment_date:
              action.payload.master_leg_terms.first_payment_date,
            next_to_last_payment_date:
              action.payload.master_leg_terms.next_to_last_payment_date,

            calculation_calendar:
              action.payload.master_leg_terms.calculation_calendar,
            reset_calendar: [defaultResetCalendarId],
          },
          leg_metrics: [],
          leg_valuation: [],
          leg_flows: [],
        },
      };
    }
    case getType(legsActions.addIrCapLeg): {
      const lastId = Object.keys(state).length;
      const defaultForecastingIndexId =
        getDefaultForecastingIndexFromCurrencyConventions(
          action.payload.leg_currency_id,
          action.payload.currency_conventions,
        );
      const defaultResetCalendarId =
        getDefaultResetCalendarIdByForecastingIndex(
          defaultForecastingIndexId,
          action.payload.ir_indices,
        );
      const defaultDiscountIndexId =
        getDefaultDiscountingIndexFromCurrencyConventions(
          action.payload.leg_currency_id,
          action.payload.currency_conventions,
        );

      return {
        ...state,
        [lastId]: {
          ...state[lastId],
          leg_terms: {
            ...empty_leg_terms,
            transaction_leg_id: v4(),
            current_notional_leg: getCurrentNotionalValueForLeg(
              action.payload.instrument_type_id!,
              LegTypes.cap,
              lastId,
            ),
            leg_type_id: LegTypes.cap,
            pay_receive_id: action.payload.pay_receive_id,
            notional: action.payload.master_leg_terms.notional,
            currency_id: action.payload.leg_currency_id
              ? action.payload.leg_currency_id
              : action.payload.master_leg_terms.currency_id,
            collateral_currency:
              action.payload.master_leg_terms.collateral_currency,
            discounting_index_id: defaultDiscountIndexId,
            day_count_id: action.payload.master_leg_terms.day_count_id,
            initial_notional_exchange:
              action.payload.master_leg_terms.initial_notional_exchange,
            intermediary_notional_exchange:
              action.payload.master_leg_terms.intermediary_notional_exchange,
            final_notional_exchange:
              action.payload.master_leg_terms.final_notional_exchange,
            // Dates
            effective_date: action.payload.master_leg_terms.effective_date,
            maturity_date: action.payload.master_leg_terms.maturity_date,
            settlement_date: action.payload.master_leg_terms.settlement_date,
            pay_frequency: action.payload.master_leg_terms.pay_frequency,
            //pay_start_end: action.payload.master_leg_terms.pay_start_end,
            payment_offset: action.payload.master_leg_terms.payment_offset,
            business_day_convention:
              action.payload.master_leg_terms.business_day_convention,
            calendar_currencies:
              action.payload.master_leg_terms.calendar_currencies,
            // Fixed Leg Fields
            calc_basis_id: null,
            coupon: null,
            // Floating Leg Fields
            forecasting_index_id: defaultForecastingIndexId,
            spread: 0.0,
            // Cap Floor Fields
            strike: 1.7,
            volatility: 0.0,
            // Inflation Fields
            //inflation_index: null,
            //initial_index: null,
            initial_payment: null,
            // schedule roll fields
            toggle_roll_fields_flag:
              action.payload.master_leg_terms.toggle_roll_fields_flag,
            eom_roll: action.payload.master_leg_terms.eom_roll,
            first_payment_date:
              action.payload.master_leg_terms.first_payment_date,
            next_to_last_payment_date:
              action.payload.master_leg_terms.next_to_last_payment_date,

            calculation_calendar:
              action.payload.master_leg_terms.calculation_calendar,
            reset_calendar: [defaultResetCalendarId],
          },
          leg_metrics: [],
          leg_valuation: [],
          leg_flows: [],
        },
      };
    }

    case getType(legsActions.addInflationLeg): {
      const lastId = Object.keys(state).length;
      const defaultForecastingIndexId =
        getDefaultForecastingIndexFromCurrencyConventions(
          action.payload.leg_currency_id,
          action.payload.currency_conventions,
        );
      const defaultResetCalendarId =
        getDefaultResetCalendarIdByForecastingIndex(
          defaultForecastingIndexId,
          action.payload.ir_indices,
        );
      const defaultDiscountIndexId =
        getDefaultDiscountingIndexFromCurrencyConventions(
          action.payload.leg_currency_id,
          action.payload.currency_conventions,
        );
      const inflationIndexForCurrency =
        action.payload.leg_currency_id === 1 ? 2 : 1;

      return {
        ...state,
        [lastId]: {
          ...state[lastId],
          leg_terms: {
            ...empty_leg_terms,
            transaction_leg_id: v4(),
            current_notional_leg: getCurrentNotionalValueForLeg(
              action.payload.instrument_type_id!,
              LegTypes.inflation,
              lastId,
            ),
            leg_type_id: LegTypes.inflation,
            pay_receive_id: action.payload.pay_receive_id,
            notional: action.payload.master_leg_terms.notional,
            currency_id: action.payload.leg_currency_id
              ? action.payload.leg_currency_id
              : action.payload.master_leg_terms.currency_id,
            collateral_currency:
              action.payload.master_leg_terms.collateral_currency,
            discounting_index_id: defaultDiscountIndexId,
            day_count_id: action.payload.master_leg_terms.day_count_id,
            initial_notional_exchange:
              action.payload.master_leg_terms.initial_notional_exchange,
            intermediary_notional_exchange:
              action.payload.master_leg_terms.intermediary_notional_exchange,
            final_notional_exchange:
              action.payload.master_leg_terms.final_notional_exchange,
            // Dates
            effective_date: action.payload.master_leg_terms.effective_date,
            maturity_date: action.payload.master_leg_terms.maturity_date,
            settlement_date: action.payload.master_leg_terms.settlement_date,
            pay_frequency: action.payload.master_leg_terms.pay_frequency,
            //pay_start_end: action.payload.master_leg_terms.pay_start_end,
            payment_offset: action.payload.master_leg_terms.payment_offset,
            business_day_convention:
              action.payload.master_leg_terms.business_day_convention,
            calendar_currencies:
              action.payload.master_leg_terms.calendar_currencies,
            // Fixed Leg Fields
            calc_basis_id: 1,
            coupon: null,
            // Floating Leg Fields
            forecasting_index_id: null,
            spread: null,
            // Cap Floor Fields
            strike: null,
            volatility: null,
            // Inflation Fields
            inflation_index: inflationIndexForCurrency,
            initial_index: 100,
            inflation_base_date:
              action.payload.master_leg_terms.inflation_base_date,
            inflation_lag: 1,
            initial_payment: null,
            // schedule roll fields
            toggle_roll_fields_flag:
              action.payload.master_leg_terms.toggle_roll_fields_flag,
            eom_roll: action.payload.master_leg_terms.eom_roll,
            first_payment_date:
              action.payload.master_leg_terms.first_payment_date,
            next_to_last_payment_date:
              action.payload.master_leg_terms.next_to_last_payment_date,

            calculation_calendar:
              action.payload.master_leg_terms.calculation_calendar,
            reset_calendar: [defaultResetCalendarId],
          },
          leg_metrics: [],
          leg_valuation: [],
          leg_flows: [],
        },
      };
    }

    case getType(legsActions.addScheduledPaymentsLeg): {
      const lastId = Object.keys(state).length;
      const hasExpiryDate =
        isDefAndNotNull(action.payload?.instrument_type_id) &&
        [
          HedgingInstrumentInterestRateMode.fxOption,
          HedgingInstrumentInterestRateMode.fxForwardNDF,
        ].includes(action.payload.instrument_type_id);
      const defaultDiscountIndexId =
        getDefaultDiscountingIndexFromCurrencyConventions(
          action.payload.leg_currency_id,
          action.payload.currency_conventions,
        );

      return {
        ...state,
        [lastId]: {
          ...state[lastId],
          leg_terms: {
            ...empty_leg_terms,
            transaction_leg_id: v4(),
            current_notional_leg: getCurrentNotionalValueForLeg(
              action.payload.instrument_type_id!,
              LegTypes.scheduledPayments,
              lastId,
            ),
            leg_type_id: LegTypes.scheduledPayments,
            pay_receive_id: action.payload.pay_receive_id,
            notional: action.payload.master_leg_terms.notional,
            currency_id: action.payload.leg_currency_id
              ? action.payload.leg_currency_id
              : action.payload.master_leg_terms.currency_id,
            collateral_currency:
              action.payload.master_leg_terms.collateral_currency,
            discounting_index_id: defaultDiscountIndexId,
            day_count_id: action.payload.master_leg_terms.day_count_id,
            initial_notional_exchange: false,
            intermediary_notional_exchange: false,
            final_notional_exchange: false,
            // Dates
            effective_date: action.payload.master_leg_terms.effective_date,
            maturity_date: action.payload.master_leg_terms.effective_date,
            settlement_date: action.payload.master_leg_terms.effective_date,
            expiry_date: hasExpiryDate
              ? action.payload.master_leg_terms.effective_date
              : null,
            pay_frequency: null,
            // pay_start_end: PaymentTypes.end,
            payment_offset: 0,
            business_day_convention: null,
            calendar_currencies:
              action.payload.master_leg_terms.calendar_currencies,
            // Fixed Leg Fields
            calc_basis_id: null,
            coupon: null,
            // Floating Leg Fields
            forecasting_index_id: null,
            spread: null,
            // Cap Floor Fields
            strike: null,
            volatility: null,
            // Inflation Fields
            //inflation_index: null,
            //initial_index: null,
            initial_payment: null,
            // schedule roll fields
            toggle_roll_fields_flag: false,
            eom_roll: true,
            first_payment_date: null,
            next_to_last_payment_date: null,

            calculation_calendar:
              action.payload.master_leg_terms.calculation_calendar,
            reset_calendar: null,
          },
          leg_metrics: [],
          leg_valuation: [],
          leg_flows: [],
        },
      };
    }

    case getType(legsActions.addCompoundingFixedLeg): {
      const lastId = Object.keys(state).length;
      const defaultIndexId = getDefaultDiscountingIndexFromCurrencyConventions(
        action.payload.leg_currency_id,
        action.payload.currency_conventions,
      );

      return {
        ...state,
        [lastId]: {
          ...state[lastId],
          leg_terms: {
            ...empty_leg_terms,
            transaction_leg_id: v4(),
            current_notional_leg: getCurrentNotionalValueForLeg(
              action.payload.instrument_type_id!,
              LegTypes.compoundingFixed,
              lastId,
            ),
            leg_type_id: LegTypes.compoundingFixed,
            pay_receive_id: action.payload.pay_receive_id,
            notional: action.payload.master_leg_terms.notional,
            currency_id: action.payload.leg_currency_id
              ? action.payload.leg_currency_id
              : action.payload.master_leg_terms.currency_id,
            collateral_currency:
              action.payload.master_leg_terms.collateral_currency,
            discounting_index_id: defaultIndexId,
            day_count_id: action.payload.master_leg_terms.day_count_id,
            initial_notional_exchange:
              action.payload.master_leg_terms.initial_notional_exchange,
            intermediary_notional_exchange:
              action.payload.master_leg_terms.intermediary_notional_exchange,
            final_notional_exchange:
              action.payload.master_leg_terms.final_notional_exchange,
            // Dates
            effective_date: action.payload.master_leg_terms.effective_date,
            maturity_date: action.payload.master_leg_terms.maturity_date,
            settlement_date: action.payload.master_leg_terms.settlement_date,
            pay_frequency: action.payload.master_leg_terms.pay_frequency,
            //pay_start_end: action.payload.master_leg_terms.pay_start_end,
            payment_offset: action.payload.master_leg_terms.payment_offset,
            business_day_convention:
              action.payload.master_leg_terms.business_day_convention,
            calendar_currencies:
              action.payload.master_leg_terms.calendar_currencies,
            // Fixed Leg Fields
            calc_basis_id: 1, // MONEY MARKET
            coupon: null,
            // Floating Leg Fields
            forecasting_index_id: null,
            spread: null,
            // Cap Floor Fields
            strike: null,
            //volatility: null,
            // Inflation Fields
            inflation_index: 2,
            initial_index: null,
            initial_payment: null,
            // Fixed compounding fields
            compounding_coupon: 0,
            // schedule roll fields
            toggle_roll_fields_flag:
              action.payload.master_leg_terms.toggle_roll_fields_flag,
            eom_roll: action.payload.master_leg_terms.eom_roll,
            first_payment_date:
              action.payload.master_leg_terms.first_payment_date,
            next_to_last_payment_date:
              action.payload.master_leg_terms.next_to_last_payment_date,
            calculation_calendar:
              action.payload.master_leg_terms.calculation_calendar,
            reset_calendar: null,
          },
          leg_metrics: [],
          leg_valuation: [],
          leg_flows: [],
        },
      };
    }

    case getType(legsActions.addPayGoLeg): {
      const lastId = Object.keys(state).length;
      const defaultForecastingIndexId =
        getDefaultForecastingIndexFromCurrencyConventions(
          action.payload.leg_currency_id,
          action.payload.currency_conventions,
        );
      const defaultResetCalendarId =
        getDefaultResetCalendarIdByForecastingIndex(
          defaultForecastingIndexId,
          action.payload.ir_indices,
        );
      const defaultDiscountIndexId =
        getDefaultDiscountingIndexFromCurrencyConventions(
          action.payload.leg_currency_id,
          action.payload.currency_conventions,
        );
      const inflationIndexForCurrency =
        action.payload.leg_currency_id === 1 ? 2 : 1;

      return {
        ...state,
        [lastId]: {
          ...state[lastId],
          leg_terms: {
            ...empty_leg_terms,
            transaction_leg_id: v4(),
            current_notional_leg: getCurrentNotionalValueForLeg(
              action.payload.instrument_type_id!,
              LegTypes.payGo,
              lastId,
            ),
            leg_type_id: LegTypes.payGo,
            pay_receive_id: action.payload.pay_receive_id,
            notional: action.payload.master_leg_terms.notional,
            currency_id: action.payload.leg_currency_id
              ? action.payload.leg_currency_id
              : action.payload.master_leg_terms.currency_id,
            collateral_currency:
              action.payload.master_leg_terms.collateral_currency,
            discounting_index_id: defaultDiscountIndexId,
            day_count_id: action.payload.master_leg_terms.day_count_id,
            initial_notional_exchange:
              action.payload.master_leg_terms.initial_notional_exchange,
            intermediary_notional_exchange:
              action.payload.master_leg_terms.intermediary_notional_exchange,
            final_notional_exchange:
              action.payload.master_leg_terms.final_notional_exchange,
            // Dates
            effective_date: action.payload.master_leg_terms.effective_date,
            maturity_date: action.payload.master_leg_terms.maturity_date,
            settlement_date: action.payload.master_leg_terms.settlement_date,
            pay_frequency: action.payload.master_leg_terms.pay_frequency,
            //pay_start_end: action.payload.master_leg_terms.pay_start_end,
            payment_offset: action.payload.master_leg_terms.payment_offset,
            business_day_convention:
              action.payload.master_leg_terms.business_day_convention,
            calendar_currencies:
              action.payload.master_leg_terms.calendar_currencies,
            // Fixed Leg Fields
            calc_basis_id: 1,
            coupon: null,
            // Floating Leg Fields
            forecasting_index_id: null,
            spread: null,
            // Cap Floor Fields
            strike: null,
            volatility: null,
            // Inflation Fields
            inflation_index: inflationIndexForCurrency,
            initial_index: 100,
            inflation_base_date:
              action.payload.master_leg_terms.inflation_base_date,
            inflation_lag: 0,
            initial_payment: 0,
            // schedule roll fields
            toggle_roll_fields_flag:
              action.payload.master_leg_terms.toggle_roll_fields_flag,
            eom_roll: action.payload.master_leg_terms.eom_roll,
            first_payment_date:
              action.payload.master_leg_terms.first_payment_date,
            next_to_last_payment_date:
              action.payload.master_leg_terms.next_to_last_payment_date,

            calculation_calendar:
              action.payload.master_leg_terms.calculation_calendar,
            reset_calendar: [defaultResetCalendarId],
          },
          leg_metrics: [],
          leg_valuation: [],
          leg_flows: [],
        },
      };
    }

    case getType(legsActions.addPremiumLeg): {
      const lastId = Object.keys(state).length;
      const defaultDiscountIndexId =
        getDefaultDiscountingIndexFromCurrencyConventions(
          action.payload.leg_currency_id,
          action.payload.currency_conventions,
        );

      return {
        ...state,
        [lastId]: {
          ...state[lastId],
          leg_terms: {
            ...empty_leg_terms,
            transaction_leg_id: v4(),
            current_notional_leg: getCurrentNotionalValueForLeg(
              action.payload.instrument_type_id!,
              LegTypes.upfrontPremium,
              lastId,
            ),
            leg_type_id: LegTypes.upfrontPremium,
            pay_receive_id: action.payload.pay_receive_id,
            notional: action.payload.master_leg_terms.notional,
            currency_id: action.payload.leg_currency_id
              ? action.payload.leg_currency_id
              : action.payload.master_leg_terms.currency_id,
            collateral_currency:
              action.payload.master_leg_terms.collateral_currency,
            discounting_index_id: defaultDiscountIndexId,
            day_count_id: null,
            initial_notional_exchange: false,
            intermediary_notional_exchange: false,
            final_notional_exchange: false,
            // Dates
            effective_date: action.payload.master_leg_terms.effective_date,
            maturity_date: action.payload.master_leg_terms.effective_date,
            settlement_date: action.payload.master_leg_terms.effective_date,
            expiry_date: action.payload.master_leg_terms.effective_date,
            pay_date: action.payload.master_leg_terms.effective_date,
            pay_frequency: null,
            payment_offset: 0,
            business_day_convention: null,
            calendar_currencies:
              action.payload.master_leg_terms.calendar_currencies,
            // Fixed Leg Fields
            calc_basis_id: null,
            coupon: null,
            // Floating Leg Fields
            forecasting_index_id: null,
            spread: null,
            // Cap Floor Fields
            strike: null,
            volatility: null,
            // Inflation Fields
            //inflation_index: null,
            //initial_index: null,
            initial_payment: null,
            // schedule roll fields
            toggle_roll_fields_flag: false,
            eom_roll: true,
            first_payment_date: null,
            next_to_last_payment_date: null,

            calculation_calendar:
              action.payload.master_leg_terms.calculation_calendar,
            reset_calendar: null,
          },
          leg_metrics: [],
          leg_valuation: [],
          leg_flows: [],
        },
      };
    }

    case getType(legsActions.addCommodityFixedLeg): {
      const lastId = Object.keys(state).length;
      const defaultIndexId = getDefaultDiscountingIndexFromCurrencyConventions(
        action.payload.leg_currency_id,
        action.payload.currency_conventions,
      );

      return {
        ...state,
        [lastId]: {
          ...state[lastId],
          leg_terms: {
            ...empty_leg_terms,
            transaction_leg_id: v4(),
            current_notional_leg: getCurrentNotionalValueForLeg(
              action.payload.instrument_type_id!,
              LegTypes.commodityFixed,
              lastId,
            ),
            leg_type_id: LegTypes.commodityFixed,
            pay_receive_id: action.payload.pay_receive_id,
            notional: 500,
            currency_id: action.payload.leg_currency_id
              ? action.payload.leg_currency_id
              : action.payload.master_leg_terms.currency_id,
            collateral_currency:
              action.payload.master_leg_terms.collateral_currency,
            discounting_index_id: defaultIndexId,
            day_count_id: action.payload.master_leg_terms.day_count_id,
            initial_notional_exchange:
              action.payload.master_leg_terms.initial_notional_exchange,
            intermediary_notional_exchange:
              action.payload.master_leg_terms.intermediary_notional_exchange,
            final_notional_exchange:
              action.payload.master_leg_terms.final_notional_exchange,
            // Dates
            effective_date: action.payload.master_leg_terms.effective_date,
            maturity_date: action.payload.master_leg_terms.maturity_date,
            settlement_date: action.payload.master_leg_terms.settlement_date,
            pay_frequency: action.payload.master_leg_terms.pay_frequency,
            //pay_start_end: action.payload.master_leg_terms.pay_start_end,
            payment_offset: action.payload.master_leg_terms.payment_offset,
            business_day_convention:
              action.payload.master_leg_terms.business_day_convention,
            calendar_currencies:
              action.payload.master_leg_terms.calendar_currencies,
            // Fixed Leg Fields
            calc_basis_id: 2,
            coupon: 20000,
            // Floating Leg Fields
            forecasting_index_id: null,
            spread: null,
            // Cap Floor Fields
            strike: null,
            //volatility: null,
            // Inflation Fields
            //inflation_index: null,
            //initial_index: null,
            initial_payment: null,
            //commodity
            commodity_index: 10,
            // schedule roll fields
            toggle_roll_fields_flag:
              action.payload.master_leg_terms.toggle_roll_fields_flag,
            eom_roll: action.payload.master_leg_terms.eom_roll,
            first_payment_date:
              action.payload.master_leg_terms.first_payment_date,
            next_to_last_payment_date:
              action.payload.master_leg_terms.next_to_last_payment_date,

            calculation_calendar:
              action.payload.master_leg_terms.calculation_calendar,
            reset_calendar: null,
          },
          leg_metrics: [],
          leg_valuation: [],
          leg_flows: [],
        },
      };
    }

    case getType(legsActions.addCommodityFloatLeg): {
      const lastId = Object.keys(state).length;
      const defaultIndexId = getDefaultDiscountingIndexFromCurrencyConventions(
        action.payload.leg_currency_id,
        action.payload.currency_conventions,
      );

      return {
        ...state,
        [lastId]: {
          ...state[lastId],
          leg_terms: {
            ...empty_leg_terms,
            transaction_leg_id: v4(),
            current_notional_leg: getCurrentNotionalValueForLeg(
              action.payload.instrument_type_id!,
              LegTypes.commodityFloat,
              lastId,
            ),
            leg_type_id: LegTypes.commodityFloat,
            pay_receive_id: action.payload.pay_receive_id,
            notional: 500,
            currency_id: action.payload.leg_currency_id
              ? action.payload.leg_currency_id
              : action.payload.master_leg_terms.currency_id,
            collateral_currency:
              action.payload.master_leg_terms.collateral_currency,
            discounting_index_id: defaultIndexId,
            day_count_id: action.payload.master_leg_terms.day_count_id,
            initial_notional_exchange:
              action.payload.master_leg_terms.initial_notional_exchange,
            intermediary_notional_exchange:
              action.payload.master_leg_terms.intermediary_notional_exchange,
            final_notional_exchange:
              action.payload.master_leg_terms.final_notional_exchange,
            // Dates
            effective_date: action.payload.master_leg_terms.effective_date,
            maturity_date: action.payload.master_leg_terms.maturity_date,
            settlement_date: action.payload.master_leg_terms.settlement_date,
            pay_frequency: action.payload.master_leg_terms.pay_frequency,
            //pay_start_end: action.payload.master_leg_terms.pay_start_end,
            payment_offset: action.payload.master_leg_terms.payment_offset,
            business_day_convention:
              action.payload.master_leg_terms.business_day_convention,
            calendar_currencies:
              action.payload.master_leg_terms.calendar_currencies,
            // Fixed Leg Fields
            calc_basis_id: 2,
            coupon: null,
            // Floating Leg Fields
            forecasting_index_id: null,
            spread: 0,
            // Cap Floor Fields
            strike: null,
            //volatility: null,
            // Inflation Fields
            //inflation_index: null,
            //initial_index: null,
            initial_payment: null,
            //commodity
            commodity_index: 10,
            // schedule roll fields
            toggle_roll_fields_flag:
              action.payload.master_leg_terms.toggle_roll_fields_flag,
            eom_roll: action.payload.master_leg_terms.eom_roll,
            first_payment_date:
              action.payload.master_leg_terms.first_payment_date,
            next_to_last_payment_date:
              action.payload.master_leg_terms.next_to_last_payment_date,

            calculation_calendar:
              action.payload.master_leg_terms.calculation_calendar,
            reset_calendar: null,
          },
          leg_metrics: [],
          leg_valuation: [],
          leg_flows: [],
        },
      };
    }

    case getType(legsActions.addCommodityCallLeg): {
      const lastId = Object.keys(state).length;
      const defaultIndexId = getDefaultDiscountingIndexFromCurrencyConventions(
        action.payload.leg_currency_id,
        action.payload.currency_conventions,
      );

      return {
        ...state,
        [lastId]: {
          ...state[lastId],
          leg_terms: {
            ...empty_leg_terms,
            transaction_leg_id: v4(),
            current_notional_leg: getCurrentNotionalValueForLeg(
              action.payload.instrument_type_id!,
              LegTypes.commodityCall,
              lastId,
            ),
            leg_type_id: LegTypes.commodityCall,
            pay_receive_id: action.payload.pay_receive_id,
            notional: 500,
            currency_id: action.payload.leg_currency_id
              ? action.payload.leg_currency_id
              : action.payload.master_leg_terms.currency_id,
            collateral_currency:
              action.payload.master_leg_terms.collateral_currency,
            discounting_index_id: defaultIndexId,
            day_count_id: action.payload.master_leg_terms.day_count_id,
            initial_notional_exchange:
              action.payload.master_leg_terms.initial_notional_exchange,
            intermediary_notional_exchange:
              action.payload.master_leg_terms.intermediary_notional_exchange,
            final_notional_exchange:
              action.payload.master_leg_terms.final_notional_exchange,
            // Dates
            effective_date: action.payload.master_leg_terms.effective_date,
            maturity_date: action.payload.master_leg_terms.maturity_date,
            settlement_date: action.payload.master_leg_terms.settlement_date,
            pay_frequency: action.payload.master_leg_terms.pay_frequency,
            //pay_start_end: action.payload.master_leg_terms.pay_start_end,
            payment_offset: action.payload.master_leg_terms.payment_offset,
            business_day_convention:
              action.payload.master_leg_terms.business_day_convention,
            calendar_currencies:
              action.payload.master_leg_terms.calendar_currencies,
            // Fixed Leg Fields
            calc_basis_id: 2,
            coupon: 0,
            // Floating Leg Fields
            forecasting_index_id: null,
            spread: 0,
            // Cap Floor Fields
            strike: 0,
            volatility: 0,
            // Inflation Fields
            //inflation_index: null,
            //initial_index: null,
            initial_payment: null,
            //commodity
            commodity_index: 10,
            digital_fields: false,
            // schedule roll fields
            toggle_roll_fields_flag:
              action.payload.master_leg_terms.toggle_roll_fields_flag,
            eom_roll: action.payload.master_leg_terms.eom_roll,
            first_payment_date:
              action.payload.master_leg_terms.first_payment_date,
            next_to_last_payment_date:
              action.payload.master_leg_terms.next_to_last_payment_date,

            calculation_calendar:
              action.payload.master_leg_terms.calculation_calendar,
            reset_calendar: null,
          },
          leg_metrics: [],
          leg_valuation: [],
          leg_flows: [],
        },
      };
    }

    case getType(legsActions.addCommodityPutLeg): {
      const lastId = Object.keys(state).length;
      const defaultIndexId = getDefaultDiscountingIndexFromCurrencyConventions(
        action.payload.leg_currency_id,
        action.payload.currency_conventions,
      );

      return {
        ...state,
        [lastId]: {
          ...state[lastId],
          leg_terms: {
            ...empty_leg_terms,
            transaction_leg_id: v4(),
            current_notional_leg: getCurrentNotionalValueForLeg(
              action.payload.instrument_type_id!,
              LegTypes.commodityPut,
              lastId,
            ),
            leg_type_id: LegTypes.commodityPut,
            pay_receive_id: action.payload.pay_receive_id,
            notional: 500,
            currency_id: action.payload.leg_currency_id
              ? action.payload.leg_currency_id
              : action.payload.master_leg_terms.currency_id,
            collateral_currency:
              action.payload.master_leg_terms.collateral_currency,
            discounting_index_id: defaultIndexId,
            day_count_id: action.payload.master_leg_terms.day_count_id,
            initial_notional_exchange:
              action.payload.master_leg_terms.initial_notional_exchange,
            intermediary_notional_exchange:
              action.payload.master_leg_terms.intermediary_notional_exchange,
            final_notional_exchange:
              action.payload.master_leg_terms.final_notional_exchange,
            // Dates
            effective_date: action.payload.master_leg_terms.effective_date,
            maturity_date: action.payload.master_leg_terms.maturity_date,
            settlement_date: action.payload.master_leg_terms.settlement_date,
            pay_frequency: action.payload.master_leg_terms.pay_frequency,
            //pay_start_end: action.payload.master_leg_terms.pay_start_end,
            payment_offset: action.payload.master_leg_terms.payment_offset,
            business_day_convention:
              action.payload.master_leg_terms.business_day_convention,
            calendar_currencies:
              action.payload.master_leg_terms.calendar_currencies,
            // Fixed Leg Fields
            calc_basis_id: 2,
            coupon: 0,
            // Floating Leg Fields
            forecasting_index_id: null,
            spread: 0,
            // Cap Floor Fields
            strike: 0,
            volatility: 0,
            // Inflation Fields
            //inflation_index: null,
            //initial_index: null,
            initial_payment: null,
            //commodity
            commodity_index: 10,
            digital_fields: false,
            // schedule roll fields
            toggle_roll_fields_flag:
              action.payload.master_leg_terms.toggle_roll_fields_flag,
            eom_roll: action.payload.master_leg_terms.eom_roll,
            first_payment_date:
              action.payload.master_leg_terms.first_payment_date,
            next_to_last_payment_date:
              action.payload.master_leg_terms.next_to_last_payment_date,

            calculation_calendar:
              action.payload.master_leg_terms.calculation_calendar,
            reset_calendar: null,
          },
          leg_metrics: [],
          leg_valuation: [],
          leg_flows: [],
        },
      };
    }

    case getType(flowsActions.updateFlowTableByCellEdit):
    case getType(flowsActions.deleteFlowRows): {
      return {
        ...state,
        [action.payload.legNumber]: {
          ...state[action.payload.legNumber],
          leg_flows: FlowsReducer(
            state[action.payload.legNumber].leg_flows,
            action,
          ),
        },
      };
    }

    case getType(pricerActions.valueTrade.success): {
      const stateWithFlows = action.payload.data.flows.reduce(
        (acc: LegsState, flows, index) => {
          acc[index] = { ...state[index], leg_flows: flows };
          return acc;
        },
        {},
      );

      return {
        ...state,
        ...stateWithFlows,
      };
    }

    case getType(pricerActions.deleteLegFlows): {
      return {
        ...state,
        [action.payload.legNumber]: {
          ...state[action.payload.legNumber],
          leg_flows: [],
        },
      };
    }

    case getType(pricerActions.copyPasteLegTerms): {
      const legs = { ...state };

      const copiedLeg = {
        ..._.omit(
          state[action.payload.copiedLegNumber].leg_terms,
          EXCLUDED_FIELDS_FOR_COPY_PASTE,
        ),
      } as LegTerms;

      Object.entries(action.payload.pastedLegNumbers).forEach(
        ([legNumber, selected]) => {
          if (!selected) return;

          legs[legNumber] = {
            ...state[legNumber],
            leg_terms: _.mapValues(
              state[legNumber].leg_terms,
              (rightValue: LegTerms[keyof LegTerms], key: keyof LegTerms) => {
                if (EXCLUDED_FIELDS_FOR_COPY_PASTE.includes(key)) {
                  return rightValue;
                }

                const leftValue = copiedLeg[key as keyof LegTerms];

                return isDefAndNotNull(leftValue) ? leftValue : rightValue;
              },
            ) as unknown as LegTerms,
          };
        },
      );

      return {
        ...state,
        ...legs,
      };
    }

    case getType(pricerActions.copyPasteLegFlows): {
      const legs = { ...state };

      const copiedLegFlows = [
        ...state[action.payload.copiedLegNumber].leg_flows,
      ].map((legFlow) => _.pick(legFlow, EXCLUDED_FIELDS_FOR_COPY_PASTE_FLOWS));

      Object.entries(action.payload.pastedLegNumbers).forEach(
        ([legNumber, selected]) => {
          if (!selected) return;

          legs[legNumber] = {
            ...state[legNumber],
            leg_flows: [...(copiedLegFlows as FlowsState)],
          };
        },
      );

      return {
        ...state,
        ...legs,
      };
    }

    case getType(legsActions.updateAllLegFlows): {
      return {
        ...state,
        [action.payload.legNumber]: {
          ...state[action.payload.legNumber],
          leg_flows: action.payload.flows,
        },
      };
    }

    default:
      return state;
  }
};

export default legsReducer;
